/**
 * DeleteUserModal
 *
 * Confirmation modal for deleting a user.
 *
 * ## Component Hierarchy
 * - **Level**: Organism
 * - **Feature**: Users
 * - **Location**: `src/features/users/components/ui/organisms/DeleteUserModal/`
 *
 * ## Features
 * - Shows confirmation message with user email and company name
 * - Calls delete API on confirmation
 * - Shows error/success messages
 *
 * @module features/users/components/ui/organisms/DeleteUserModal
 */
import React, { useCallback } from 'react';
import { App, Modal } from 'antd';
import { useLanguage } from '@/shared/hooks/useLanguage';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import type { BusinessAccountUser } from '@/features/users/types/users.types';
import { deleteUser } from '@/features/users/services/businessAccountUsers';
import { BUSINESS_ACCOUNT_USERS_QUERY_KEY } from '@/features/users/hooks/useBusinessAccountUsers';

import './DeleteUserModal.css';

export interface DeleteUserModalProps {
  /** Whether the modal is visible */
  open: boolean;
  /** The user to delete */
  user: BusinessAccountUser | null;
  /** Business account name to display in the confirmation message */
  businessAccountName?: string;
  /** Callback when modal is closed */
  onClose: () => void;
  /** Callback when deletion is successful */
  onSuccess?: () => void;
}

/**
 * DeleteUserModal Component
 *
 * @param props - Component properties
 * @returns React component
 *
 * @example
 * ```tsx
 * <DeleteUserModal
 *   open={isOpen}
 *   user={selectedUser}
 *   businessAccountName="Empresa LTDA"
 *   onClose={() => setIsOpen(false)}
 *   onSuccess={() => refetchUsers()}
 * />
 * ```
 */
export const DeleteUserModal: React.FC<DeleteUserModalProps> = ({
  open,
  user,
  businessAccountName,
  onClose,
  onSuccess,
}) => {
  const { t } = useLanguage();
  const { message } = App.useApp();
  const queryClient = useQueryClient();

  const { mutateAsync: doDelete, isPending } = useMutation({
    mutationFn: (userId: string) => deleteUser(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: BUSINESS_ACCOUNT_USERS_QUERY_KEY,
      });
    },
  });

  const handleConfirm = useCallback(async () => {
    if (!user) return;

    try {
      await doDelete(user.id);
      message.success(t('User deleted successfully!'));
      onSuccess?.();
      onClose();
    } catch {
      message.error(t('Error deleting user!'));
    }
  }, [user, doDelete, message, t, onSuccess, onClose]);

  const userEmail = user?.email?.email ?? '';

  return (
    <Modal
      open={open}
      title={t('Delete User')}
      onCancel={onClose}
      onOk={handleConfirm}
      okText={t('Delete')}
      cancelText={t('Close')}
      okButtonProps={{
        danger: true,
        loading: isPending,
        className: 'delete-user-modal__action-btn',
      }}
      cancelButtonProps={{
        className: 'delete-user-modal__close-btn',
      }}
      destroyOnHidden
      className="delete-user-modal"
      aria-label={t('Delete user confirmation modal')}
    >
      <div className="delete-user-modal__content">
        <p className="delete-user-modal__question">
          {t('Are you sure you want to delete the user')}{' '}
          <span className="delete-user-modal__highlight">{userEmail}</span>{' '}
          {t('from the company')}{' '}
          <span className="delete-user-modal__highlight">
            {businessAccountName ?? ''}
          </span>
          ?
        </p>
      </div>
    </Modal>
  );
};

export default DeleteUserModal;
